import {FC, JSX} from 'react';
import {createRoot, Root} from 'react-dom/client';
import {styled} from 'styled-components';

export interface NotificationOptions {
  duration?: number;
  minDuration?: number;
  maxDuration?: number;
  wordsPerMinute?: number;
}

let baseOptions: NotificationOptions = {
  minDuration: 2000,
  maxDuration: 10000,
  wordsPerMinute: 200, // Average reading speed
};

const mergeOptions = (overrides?: Partial<NotificationOptions>): NotificationOptions => ({
  duration: overrides?.duration,
  minDuration: overrides?.minDuration ?? baseOptions.minDuration,
  maxDuration: overrides?.maxDuration ?? baseOptions.maxDuration,
  wordsPerMinute: overrides?.wordsPerMinute ?? baseOptions.wordsPerMinute,
});

export function setupNotifications(overrides?: Partial<NotificationOptions>): void {
  if (overrides) {
    baseOptions = mergeOptions(overrides);
  }
}

type NotificationType = 'alert' | 'success';
type NotificationContent = JSX.Element | string;

interface NotificationItem {
  id: number;
  type: NotificationType;
  content: NotificationContent;
  duration: number;
  isLeaving: boolean;
}

let notificationId = 0;
let containerRoot: Root | null = null;
let containerElement: HTMLDivElement | null = null;
const notifications: NotificationItem[] = [];

function ensureContainer(): void {
  if (!containerElement) {
    containerElement = document.createElement('div');
    document.body.appendChild(containerElement);
    containerRoot = createRoot(containerElement);
    renderNotifications();
  }
}

function renderNotifications(): void {
  if (containerRoot) {
    containerRoot.render(
      <NotificationContainer items={notifications} onRemove={removeNotification} />
    );
  }
}

function removeNotification(id: number): void {
  const index = notifications.findIndex(n => n.id === id);
  if (index !== -1) {
    // Mark as leaving to trigger exit animation
    const notification = notifications[index];
    if (notification) {
      notification.isLeaving = true;
      renderNotifications();
    }

    // Remove from DOM after animation completes
    setTimeout(() => {
      const currentIndex = notifications.findIndex(n => n.id === id);
      if (currentIndex !== -1) {
        notifications.splice(currentIndex, 1);
        renderNotifications();
      }
    }, 300); // Match animation duration
  }
}

export function showAlert(
  content: NotificationContent,
  overrides?: Partial<NotificationOptions>
): void {
  showNotification('alert', content, mergeOptions(overrides));
}

export function showSuccess(
  content: NotificationContent,
  overrides?: Partial<NotificationOptions>
): void {
  showNotification('success', content, mergeOptions(overrides));
}

function calculateDuration(content: NotificationContent, options: NotificationOptions): number {
  // If duration is explicitly provided, use it
  if (options.duration !== undefined) {
    return options.duration;
  }

  // Extract text content
  let text = '';
  if (typeof content === 'string') {
    text = content;
  } else {
    // For JSX elements, we'll use a rough estimate based on the element tree
    // In a real scenario, you might want to render and extract text content
    text = content.toString();
  }

  // Calculate reading time based on word count
  const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
  const wordsPerMinute = options.wordsPerMinute ?? 200;
  const readingTimeMs = (wordCount / wordsPerMinute) * 60 * 1000;

  // Add some buffer time for comprehension
  const durationMs = readingTimeMs + 1000;

  // Clamp between min and max
  const minDuration = options.minDuration ?? 2000;
  const maxDuration = options.maxDuration ?? 10000;

  return Math.max(minDuration, Math.min(maxDuration, durationMs));
}

function showNotification(
  type: NotificationType,
  content: NotificationContent,
  options: NotificationOptions
): void {
  ensureContainer();

  const duration = calculateDuration(content, options);

  const item: NotificationItem = {
    id: notificationId++,
    type,
    content,
    duration,
    isLeaving: false,
  };

  notifications.unshift(item);
  renderNotifications();

  setTimeout(() => {
    removeNotification(item.id);
  }, item.duration);
}

interface NotificationContainerProps {
  items: NotificationItem[];
  onRemove: (id: number) => void;
}

const NotificationContainer: FC<NotificationContainerProps> = props => {
  const {items} = props;

  return (
    <Container>
      {items.map(item => (
        <Notification
          key={item.id}
          type={item.type}
          content={item.content}
          isLeaving={item.isLeaving}
        />
      ))}
    </Container>
  );
};
NotificationContainer.displayName = 'NotificationContainer';

interface NotificationProps {
  type: NotificationType;
  content: NotificationContent;
  isLeaving: boolean;
}

const Notification: FC<NotificationProps> = props => {
  const {type, content, isLeaving} = props;

  return (
    <Notif data-test-id="notification" $type={type} $isLeaving={isLeaving}>
      {content}
    </Notif>
  );
};
Notification.displayName = 'Notification';

const Container = styled.div`
  position: fixed;
  top: 62px;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  z-index: 9999;
  pointer-events: none;
`;

const Notif = styled.div<{$type: NotificationType; $isLeaving: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-radius: 0;
  color: white;
  font-size: 16px;
  min-width: 200px;
  min-height: 60px;
  width: fit-content;
  pointer-events: all;
  border: 1px solid white;
  background-color: ${p =>
    p.$type === 'alert'
      ? 'var(--notification-alert-color, #D51633)'
      : 'var(--notification-success-color, #34C759)'};
  
  animation: ${p => (p.$isLeaving ? 'slideOut' : 'slideIn')} 300ms ease-in-out;
  transform: ${p => (p.$isLeaving ? 'translateX(100%)' : 'translateX(0)')};
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`;
